import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import defaultProductImage from "../../images/Default-Image.jpeg";
import { saveCartItems } from "../../utils/cartUtils";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import "./OrderSuccess.css";

const OrderSuccess = () => {
  const showPrices = useCanViewPrices();
  const navigate = useNavigate();
  const { state } = useLocation();
  const data = state || {};
  const api = data.apiResponse || {};

  useEffect(() => {
    if (!state) {
      navigate("/", { replace: true });
    }
  }, [state, navigate]);

  const orderId = data.orderId ?? api.OrderId ?? "—";
  const orderNumber = data.orderNumber ?? api.TxnId ?? api.txnId ?? orderId;
  const status = data.status ?? "PENDING";
  const orderDateRaw = api.OrderDate ?? data.orderDate;
  const orderDate = orderDateRaw
    ? new Date(orderDateRaw).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const cartItemsFromState = data.items || [];
  const itemsFromApi = Array.isArray(api.ItemDetail)
    ? api.ItemDetail.map((row) => ({
        id: row.ItemId,
        ItemId: row.ItemId,
        name: row.ItemName,
        price: row.UnitPrice,
        quantity: row.Quantity,
        TotalPrice: row.TotalPrice,
        sku: row.ItemCode,
      }))
    : [];
  // When API returns ItemDetail, merge with cart items so we keep product images
  const items =
    itemsFromApi.length > 0
      ? itemsFromApi.map((apiItem, index) => {
          const cartItem = cartItemsFromState.find(
            (c) => String(c.id) === String(apiItem.ItemId) || String(c.id) === String(apiItem.id)
          ) || cartItemsFromState[index];
          return {
            ...apiItem,
            image: cartItem?.image || defaultProductImage,
          };
        })
      : cartItemsFromState;
  const subtotalFromApi =
    itemsFromApi.length > 0
      ? itemsFromApi.reduce((sum, i) => sum + Number(i.TotalPrice ?? i.price * i.quantity ?? 0), 0)
      : Number(api.TotalAmount) - Number(api.TaxAmount ?? 0) - Number(api.ShippingAmount ?? 0);
  const subtotal = Number(subtotalFromApi) || Number(data.subtotal) || 0;
  const shipping = Number(api.ShippingAmount ?? data.shipping) || 0;
  const taxes = Number(api.TaxAmount ?? data.taxes) || 0;
  const total = Number(api.TotalAmount ?? data.total) || subtotal + shipping + taxes;
  const shippingDetail = api.ShippingDetail || {};
  const shippingAddress = {
    name: shippingDetail.RecipientName ?? data.shippingAddress?.name,
    address: [shippingDetail.StreetAddress, shippingDetail.City, shippingDetail.State, shippingDetail.PostalCode]
      .filter(Boolean)
      .join(", "),
    country: shippingDetail.Country ?? data.shippingAddress?.country,
    phone: shippingDetail.PhoneNumber ?? data.shippingAddress?.phone,
  };
  const billingDetail = api.BillingDetail || data.billingDetail || {};
  const paymentMethod = data.paymentMethod || "Credit Card";

  const handlePrint = () => {
    window.print();
  };

  const handleReorder = () => {
    const cartItems = items.map((item) => ({
      id: item.id ?? item.ItemId,
      name: item.name ?? item.ItemName ?? "Item",
      price: Number(item.price ?? item.UnitPrice ?? 0),
      quantity: Number(item.quantity ?? item.Quantity ?? 1),
      image: item.image || defaultProductImage,
      ...(item.sku ?? item.ItemCode ? { sku: item.sku ?? item.ItemCode } : {}),
    }));
    saveCartItems(cartItems);
    navigate("/checkout");
  };

  return (
    <div className="order-success-page">
      <div className="order-success-card">
        <h1 className="order-success-title">Thank you for your purchase!</h1>

        <div className="order-success-header-row">
          <div className="order-success-meta">
            <span className="order-success-id">
              Order #{typeof orderNumber === "string" && orderNumber.length > 0 ? orderNumber : String(orderNumber).padStart(9, "0")}
            </span>
            <span className={`order-success-status order-success-status-${status.toLowerCase()}`}>{status}</span>
            <span className="order-success-date">{orderDate}</span>
          </div>
          <div className="order-success-actions">
            <button type="button" className="order-success-btn order-success-btn-reorder" onClick={handleReorder}>
              Reorder
            </button>
            <button type="button" className="order-success-btn order-success-btn-print" onClick={handlePrint}>
              Print Order
            </button>
          </div>
        </div>

        <section className="order-success-section">
          <h2 className="order-success-section-title">Order Details</h2>
          <div className="order-success-details-wrap">
            <div className="order-success-table-wrap">
              <table className="order-success-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    {showPrices && <th>Price</th>}
                    <th>Qty</th>
                    {showPrices && <th>Subtotal</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id || item.ItemId}>
                      <td>
                        <div className="order-success-product-cell">
                          <div className="order-success-product-img">
                            <img
                              src={item.image || defaultProductImage}
                              alt={item.name ?? item.ItemName ?? "Item"}
                            />
                          </div>
                          <div>
                            <span className="order-success-product-name">{item.name ?? item.ItemName ?? "Item"}</span>
                            {(item.sku ?? item.ItemCode) && (
                              <span className="order-success-product-sku">SKU: {item.sku ?? item.ItemCode}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      {showPrices && (
                        <td>${(item.price ?? item.UnitPrice ?? 0).toFixed(2)}</td>
                      )}
                      <td>{item.quantity ?? item.Quantity ?? 1}</td>
                      {showPrices && (
                        <td>
                          $
                          {(item.TotalPrice ?? (item.price ?? item.UnitPrice ?? 0) * (item.quantity ?? item.Quantity ?? 1)).toFixed(2)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="order-success-totals">
              {showPrices && (
                <>
                  <div className="order-success-total-row">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="order-success-total-row">
                    <span>Shipping</span>
                    <span>${shipping.toFixed(2)}</span>
                  </div>
                  <div className="order-success-total-row">
                    <span>Taxes</span>
                    <span>${taxes.toFixed(2)}</span>
                  </div>
                  <div className="order-success-total-row order-success-total-final">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="order-success-section">
          <h2 className="order-success-section-title">Order Information</h2>
          <div className="order-success-info-grid">
            <div className="order-success-info-block">
              <h3 className="order-success-info-label">Shipping Address</h3>
              <p className="order-success-info-text">
                {shippingAddress.name || "—"}
                <br />
                {shippingAddress.address || "—"}
                <br />
                {shippingAddress.country || "—"}
                {shippingAddress.phone && (
                  <>
                    <br />
                    T: {shippingAddress.phone}
                  </>
                )}
              </p>
            </div>
            <div className="order-success-info-block">
              <h3 className="order-success-info-label">Shipping Method</h3>
              <p className="order-success-info-text">Standard Shipping</p>
            </div>
            <div className="order-success-info-block">
              <h3 className="order-success-info-label">Billing Address</h3>
              <p className="order-success-info-text">
                {billingDetail.RecipientName || "—"}
                <br />
                {billingDetail.StreetAddress || "—"}
                <br />
                {[billingDetail.City, billingDetail.State, billingDetail.PostalCode].filter(Boolean).join(", ") || "—"}
                <br />
                {billingDetail.Country || "—"}
              </p>
            </div>
            <div className="order-success-info-block">
              <h3 className="order-success-info-label">Payment Method</h3>
              <p className="order-success-info-text">{paymentMethod}</p>
            </div>
          </div>
        </section>

        <div className="order-success-footer-actions">
          <button type="button" className="order-success-btn order-success-btn-primary" onClick={() => navigate("/")}>
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
